import { findUsageEvent } from '@/utils/usageEventKeys'
import { authStateOf, buildEvaluationContext } from '@/utils/featureFlagContext'

/**
 * Usage analytics: what buyers do with market context and market intelligence, sent to Datadog
 * Product Analytics (RUM actions, for the counts) and to Microsoft Clarity (custom events and tags,
 * for watching recordings). See docs/MARKET_CONTEXT_USAGE_ANALYTICS.md.
 *
 *   this.$track(USAGE_EVENTS.marketBannerReviewClicked.name)
 *   this.$track(USAGE_EVENTS.marketSuggestionOpened.name, { campaignId, ... })
 *   this.$trackOnce(USAGE_EVENTS.marketCardShown.name, state, { ... })   // once per instance per key
 *   this.$trackOnce(USAGE_EVENTS.marketListShown.name, 'load', () => ({ ... }))   // built only if sent
 *
 * Every event carries, under `flags`, what each feature flag reads as now, from
 * `$featureFlags.inspect()`, which evaluates nothing and so logs no exposure. A market event fires
 * after its gate has read the flags it depends on; a flag no view has read carries its fallback.
 *
 * The RUM SDK is loaded and started once a page load, the first time a dashboard page (`start`, from
 * the layout) is a buyer account's, so a public page, a seller and staff on the Impersonate page never
 * download it or contact Datadog. Tracking consent is granted for a buyer account and withdrawn for
 * any other, and at sign-out, which also clears the user and the global context. Of what RUM collects
 * itself, views go named by route with no query on their URLs, and errors not at all (Sentry has them).
 * Nothing is sent where the build is not configured (local development) or VUE_APP_DD_RUM_ENABLED
 * is not `true`. Clarity gets the events in the builds where `Clarity.init` runs, which hand it in.
 *
 * Nothing here throws into a component, except an unknown event name outside production builds.
 */

// What the RUM side is doing. Clarity has no state of its own here.
export const USAGE_ANALYTICS_STATUS = Object.freeze({
  // Not configured for this build, or VUE_APP_DD_RUM_ENABLED is not `true`.
  DISABLED: 'DISABLED',
  // Configured, and not started: no dashboard page has been a buyer's yet this page load. Events
  // wait, as a buyer's account details may arrive after its first view.
  IDLE: 'IDLE',
  // The SDK's chunk is loading. Events wait for it.
  LOADING: 'LOADING',
  STARTED: 'STARTED',
  // The SDK could not be loaded or started. Events go to Clarity only.
  ERROR: 'ERROR'
})

const SERVICE = 'ringba-x-client-ui'

// Events sent before the SDK starts wait for it, up to this many.
const PENDING_LIMIT = 100

const warnedUnknownNames = new Set()

function readConfig() {
  return {
    clientToken: process.env.VUE_APP_DD_CLIENT_TOKEN,
    applicationId: process.env.VUE_APP_DD_APPLICATION_ID,
    env: process.env.VUE_APP_DD_ENV,
    site: process.env.VUE_APP_DD_SITE || undefined,
    rumEnabled: process.env.VUE_APP_DD_RUM_ENABLED === 'true'
  }
}

// Fetched only for a configured build, once the dashboard layout starts it: a seller, a public page
// and local development never download it.
async function loadDatadogRum() {
  const { datadogRum } = await import(/* webpackChunkName: "usage-analytics" */ '@datadog/browser-rum-slim')

  return datadogRum
}

// A route's view name. A route pattern, never a URL: each campaign's edit page is one view.
function viewName(route) {
  return route.name || route.matched[route.matched.length - 1]?.path || 'unnamed'
}

// RUM's `beforeSend`, for what it collects itself, which the registry never sees: no errors, whose
// messages can hold a campaign's name (Sentry has them), and no query or fragment on a view's URL or
// referrer, where a list's filter or a search could. False discards the event.
export function filterRumEvent(event) {
  if (event.type === 'error') {
    return false
  }

  if (event.view) {
    for (const field of ['url', 'referrer']) {
      if (typeof event.view[field] === 'string') {
        event.view[field] = event.view[field].split(/[?#]/)[0]
      }
    }
  }

  return true
}

/**
 * The analytics for one store and router. `install` creates one; the tests drive it directly.
 *
 * @param {object} options
 * @param {import('vuex').Store} options.store
 * @param {import('vue-router').default} [options.router]
 * @param {() => Array<{ key: string, value: unknown }>} [options.flagValues] the feature flags as
 *   `$featureFlags.inspect()` reports them
 * @param {object} [options.config] the Datadog settings; the build's environment by default
 * @param {() => Promise<object>} [options.loadRum] resolves to `datadogRum`
 * @param {object|null} [options.clarity] the Clarity API where `Clarity.init` ran, null elsewhere
 */
export function createUsageAnalytics({
  store,
  router = null,
  flagValues = () => [],
  config = readConfig(),
  loadRum = loadDatadogRum,
  clarity = null
}) {
  const rumConfigured = Boolean(config.rumEnabled && config.clientToken && config.applicationId && config.env)

  let status = rumConfigured ? USAGE_ANALYTICS_STATUS.IDLE : USAGE_ANALYTICS_STATUS.DISABLED
  let rum = null
  let pending = []
  let unwatch = null
  let removeAfterEach = null

  // Whether the dashboard layout has started a session since the last sign-in. Consent is granted
  // only then, so a login does not start a session on the login page.
  let active = false
  // Whether the user and the global context are set, so sign-out has something to clear.
  let identified = false

  // Component -> the once-keys it has sent.
  const sentOnce = new WeakMap()

  // Never throws: analytics must not take a view down.
  function guarded(vendor, call) {
    try {
      call()
    } catch (error) {
      console.warn(`Usage analytics: ${vendor} call failed`, error)
    }
  }

  function currentFlags() {
    try {
      return Object.fromEntries(flagValues().map(({ key, value }) => [key, value]))
    } catch (error) {
      return {}
    }
  }

  // Only the keys the registry lists, and none left undefined. Null stays: "no suggestion" is data.
  function payloadFor(definition, context) {
    const payload = {}

    for (const key of definition.context) {
      if (context[key] !== undefined) {
        payload[key] = context[key]
      }
    }

    payload.flags = currentFlags()

    return payload
  }

  function sendToRum(name, payload) {
    if (status === USAGE_ANALYTICS_STATUS.STARTED) {
      guarded('Datadog', () => rum.addAction(name, payload))
    } else if ((status === USAGE_ANALYTICS_STATUS.IDLE || status === USAGE_ANALYTICS_STATUS.LOADING) && pending.length < PENDING_LIMIT) {
      pending.push([name, payload])
    }
  }

  function sendToClarity(name, definition, payload) {
    if (!clarity) {
      return
    }

    guarded('Clarity', () => {
      clarity.event(name)

      for (const [tag, value] of Object.entries(definition.clarityTags?.(payload) || {})) {
        if (value != null) {
          clarity.setTag(tag, String(value))
        }
      }
    })
  }

  /**
   * Sends one event. A name missing from `USAGE_EVENTS` is a bug: it throws outside production
   * builds so a unit test or a dev session trips over it, and in production it warns once.
   */
  function track(name, context = {}) {
    const definition = findUsageEvent(name)

    if (!definition) {
      const message = `Unknown usage event "${name}". Add it to USAGE_EVENTS in src/utils/usageEventKeys.js.`

      if (process.env.NODE_ENV !== 'production') {
        throw new Error(message)
      }

      if (!warnedUnknownNames.has(name)) {
        warnedUnknownNames.add(name)
        console.warn(message)
      }

      return
    }

    const payload = payloadFor(definition, context || {})

    sendToRum(name, payload)
    sendToClarity(name, definition, payload)
  }

  /**
   * Sends an event once per component per `onceKey`, for the `*_shown` events: a re-render does not
   * count twice, and a new key (a new state, another category) does count. `context` may be a
   * function, called only when the event is sent, for a context that is costly to build.
   */
  function trackOnce(component, name, onceKey, context) {
    const key = `${name}\u0000${onceKey}`
    let sent = sentOnce.get(component)

    if (!sent) {
      sent = new Set()
      sentOnce.set(component, sent)
    }

    if (sent.has(key)) {
      return
    }

    sent.add(key)
    track(name, typeof context === 'function' ? context() : context)
  }

  // What the watch below compares: the auth state the context is built from, and the account type
  function authKey() {
    return JSON.stringify([authStateOf(store), store.getters['auth/isBuyerAccount']])
  }

  // Withdrawn consent ends the session. Stopping it instead would not do: RUM starts a new one at
  // the next interaction, which would be on the login page. Events still waiting for the SDK were
  // this user's, and go too: RUM would hold them for the next user to sign in in this tab.
  function signOut() {
    pending = []

    if (identified) {
      guarded('Datadog', () => {
        rum.setTrackingConsent('not-granted')
        rum.clearUser()
        rum.clearGlobalContext()
      })
    }

    identified = false
    active = false
  }

  // Brings the SDK in line with the auth state: starts it for the first buyer account, and sets the
  // user, the global context and the consent. Called from the layout's start-up and whenever that
  // state changes: a late account, a login without a reload, a sign-out. Switching accounts and
  // impersonation reload the page.
  function sync() {
    const auth = authStateOf(store)

    if (!auth.isAuthenticated) {
      signOut()

      return Promise.resolve()
    }

    // Not since the last sign-in: a login lands here on the login page, before the layout
    if (!active) {
      return Promise.resolve()
    }

    const isBuyer = store.getters['auth/isBuyerAccount'] === true

    if (status === USAGE_ANALYTICS_STATUS.IDLE) {
      return isBuyer ? init() : Promise.resolve()
    }

    // `null` while the user's details load
    const context = status === USAGE_ANALYTICS_STATUS.STARTED ? buildEvaluationContext(auth) : null

    if (!context) {
      return Promise.resolve()
    }

    // The flags' attributes but the targeting key and the user id: the user is RUM's own
    const globalContext = { ...context }
    delete globalContext.targetingKey
    delete globalContext.userId

    guarded('Datadog', () => {
      // The id only: the flag provider adds RUM's user to the flags' evaluation context
      rum.setUser({ id: context.userId })
      rum.setGlobalContext(globalContext)
      rum.setTrackingConsent(isBuyer ? 'granted' : 'not-granted')
    })

    identified = true

    return Promise.resolve()
  }

  function startViews() {
    // Views by route name. Automatic views would name each campaign's edit page from its URL
    guarded('Datadog', () => rum.startView({ name: viewName(router.currentRoute) }))

    removeAfterEach = router.afterEach((to, from) => {
      // A query change (a list's filter, a tab) is the same page
      if (to.path === from.path && to.name === from.name) {
        return
      }

      guarded('Datadog', () => rum.startView({ name: viewName(to) }))
    })
  }

  // Once a page load
  async function init() {
    status = USAGE_ANALYTICS_STATUS.LOADING

    try {
      rum = await loadRum()

      rum.init({
        applicationId: config.applicationId,
        clientToken: config.clientToken,
        site: config.site,
        env: config.env,
        service: SERVICE,
        // B2B volumes: sampling would drop whole accounts
        sessionSampleRate: 100,
        // Sentry and Clarity already record
        sessionReplaySampleRate: 0,
        // Automatic actions are named from a button's text, which holds campaign names. Only the
        // registry's events are sent as actions
        trackUserInteractions: false,
        trackResources: false,
        trackLongTasks: false,
        defaultPrivacyLevel: 'mask',
        trackViewsManually: true,
        // Granted by `sync` while the session is a buyer's
        trackingConsent: 'not-granted',
        beforeSend: filterRumEvent
        // No allowedTracingUrls: no trace headers on API calls. No trackFeatureFlagsForEvents: the
        // events carry the flag values themselves
      })
    } catch (error) {
      console.warn('Usage analytics: the Datadog RUM SDK could not be loaded or started. Events go to Clarity only.', error)

      status = USAGE_ANALYTICS_STATUS.ERROR
      pending = []

      return
    }

    status = USAGE_ANALYTICS_STATUS.STARTED

    if (router) {
      startViews()
    }

    // The auth state as it is now, which may have moved on while the chunk loaded
    await sync()

    // Sent before the SDK started, by this user: sign-out empties the queue. RUM holds them until
    // consent is granted, and drops them if it never is
    for (const [name, payload] of pending) {
      guarded('Datadog', () => rum.addAction(name, payload))
    }

    pending = []
  }

  /**
   * The dashboard layout's start-up, each time it is created: marks the session as one to send for
   * once it is a buyer's, and starts the SDK the first time it is.
   */
  function start() {
    if (status === USAGE_ANALYTICS_STATUS.DISABLED) {
      return Promise.resolve()
    }

    active = true

    if (!unwatch) {
      unwatch = store.watch(authKey, () => sync())
    }

    // Views again after a `stop`: the SDK's first start adds them
    if (status === USAGE_ANALYTICS_STATUS.STARTED && router && !removeAfterEach) {
      startViews()
    }

    return sync()
  }

  // Only the tests need this.
  function stop() {
    unwatch?.()
    removeAfterEach?.()

    unwatch = null
    removeAfterEach = null
  }

  // The tag that tells staging's sessions from production's in the shared Clarity project, once a
  // page load
  if (clarity && config.env) {
    guarded('Clarity', () => clarity.setTag('env', config.env))
  }

  return {
    track,
    trackOnce,
    start,
    stop,

    get status() {
      return status
    }
  }
}

export default {
  // `config` is the build's environment unless given, which only the tests do
  install(Vue, { store, router, clarity = null, config }) {
    const analytics = createUsageAnalytics({
      store,
      router,
      clarity,
      config,
      // Read at each event, so the flags plugin may be installed after this one
      flagValues: () => Vue.prototype.$featureFlags?.inspect() || []
    })

    Vue.prototype.$usageAnalytics = analytics
    Vue.prototype.$track = (name, context) => analytics.track(name, context)
    Vue.prototype.$trackOnce = function (name, onceKey, context) {
      analytics.trackOnce(this, name, onceKey, context)
    }
  }
}
